import { useMemo } from 'react';

interface HeatmapItem {
  hour: number;
  count: number;
}

interface UseHourlyHeatmapReturn {
  hours: HeatmapItem[];
  maxCount: number;
  getColorClass: (count: number) => string;
}

export const useHourlyHeatmap = (
  data: HeatmapItem[]
): UseHourlyHeatmapReturn => {
  const normalizedData = useMemo(() => {
    const TZ_OFFSET = 0;
    const map = new Map(
      data.map((d) => {
        const adjustedHour = (d.hour + TZ_OFFSET + 24) % 24;
        return [adjustedHour, d.count];
      })
    );

    const result: HeatmapItem[] = [];
    let max = 0;

    for (let i = 9; i <= 19; i++) {
      const count = map.get(i) || 0;
      result.push({ hour: i, count });
      if (count > max) max = count;
    }
    return { hours: result, maxCount: max };
  }, [data]);

  const getColorClass = (count: number) => {
    if (count === 0) return 'bg-slate-50 border-slate-100';
    const ratio = count / (normalizedData.maxCount || 1);

    if (ratio < 0.2) return 'bg-orange-100 border-orange-200 text-orange-900';
    if (ratio < 0.4) return 'bg-orange-300 border-orange-400 text-white';
    if (ratio < 0.6) return 'bg-orange-500 border-orange-600 text-white';
    if (ratio < 0.8) return 'bg-orange-700 border-orange-800 text-white';
    return 'bg-orange-900 border-orange-950 text-white';
  };

  return {
    hours: normalizedData.hours,
    maxCount: normalizedData.maxCount,
    getColorClass,
  };
};
